import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  SharedFeatureService,
} from '@feature-hub/core';
import type {LocaleServiceV3} from '@smart/locale-service';
import type {PubSubServiceV1} from '@smart/pub-sub-service';
import Cookies from 'js-cookie';
import {createRandomValue} from './create-random-value.js';
import type {LoginStateV2, LoginStateV3} from './parse-login-state.js';
import {parseLoginState} from './parse-login-state.js';

export interface LoginServiceV1 {
  readonly login: () => void;
  readonly logout: () => void;
  readonly isLoggedIn: boolean;
}

export interface LoginServiceV2 {
  readonly login: () => void;
  readonly logout: (redirectPath?: string) => void;
  readonly loginState: LoginStateV2 | undefined;
}

export interface LoginServiceV3 {
  readonly login: () => void;
  readonly logout: (redirectPath?: string) => void;
  readonly loginState: LoginStateV3;
}

export type {LoginStateV2, LoginStateV3};

export type {
  LoggedOutStateV3,
  LoginErrorStateV3,
  LoggedInStateV3,
} from './parse-login-state.js';

export interface SharedLoginService extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<LoginServiceV1>;
  readonly '2.1.0': FeatureServiceBinder<LoginServiceV2>;
  readonly '3.0.0': FeatureServiceBinder<LoginServiceV3>;
}

export interface LoginServiceDependencies extends FeatureServices {
  readonly 'locale-service': LocaleServiceV3;
  readonly 'pub-sub'?: PubSubServiceV1;
}

export interface LoginServiceOptions {
  readonly authorizePathname: string;
  readonly logoutPathname: string;
  readonly authorizeStateCookieName: string;
  readonly loginStateCookieName: string;
}

export function defineLoginService(
  options: LoginServiceOptions,
): FeatureServiceProviderDefinition<
  SharedLoginService,
  LoginServiceDependencies
> {
  const {
    authorizePathname,
    logoutPathname,
    authorizeStateCookieName,
    loginStateCookieName,
  } = options;

  return {
    id: `login-service`,

    dependencies: {
      featureServices: {
        'locale-service': `^3.0.0`,
      },
    },

    optionalDependencies: {
      featureServices: {
        'pub-sub': `^1.0.0`,
      },
    },

    create: ({featureServices}) => {
      const localeService = featureServices[`locale-service`];
      const pubSubService = featureServices[`pub-sub`];

      const login = () => {
        pubSubService?.publish(`before-login`);

        const {languageTag} = localeService.getLocale();
        const {origin, pathname, search} = document.location;

        const state = JSON.stringify([
          createRandomValue(),
          `${pathname}${search}`,
        ]);

        Cookies.set(authorizeStateCookieName, state, {path: `/`});

        const authorizeUrl = new URL(authorizePathname, origin);
        authorizeUrl.searchParams.set(`languageTag`, languageTag);

        window.location.assign(authorizeUrl.href);
      };

      const logout = (
        redirectPath: string = window.location.href.replace(
          window.location.origin,
          ``,
        ),
      ) => {
        window.location.assign(
          `${logoutPathname}?redirectPath=${encodeURIComponent(redirectPath)}`,
        );
      };

      const loginState = parseLoginState(loginStateCookieName);
      const isLoggedIn = loginState.status === `logged-in`;

      const loginStateV2: LoginStateV2 | undefined =
        loginState.status === `logged-in`
          ? {uid: loginState.uid, customerType: loginState.customerType}
          : undefined;

      // https://web.dev/bfcache/#update-stale-or-sensitive-data-after-bfcache-restore
      window.addEventListener(`pageshow`, (event) => {
        if (
          event.persisted &&
          loginState.status !== parseLoginState(loginStateCookieName).status
        ) {
          location.reload();
        }
      });

      return {
        '1.0.0': () => ({
          featureService: {login, logout, isLoggedIn},
        }),

        '2.1.0': () => ({
          featureService: {login, logout, loginState: loginStateV2},
        }),

        '3.0.0': () => ({
          featureService: {login, logout, loginState},
        }),
      };
    },
  };
}
